import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * NewProjectModal (§3.H — `view/new_project.js` + `new_project.tmpl`).
 *
 * Opened by ContainerHeader (`new NewProject().show()`); re-instantiated each
 * open (here: mounted only while `dashboardSlice.modals.newProject` is true, so
 * the default name + focus/select run fresh every open).
 *
 *   div.popup-wrapper.create-popup.active > .popup-content
 *     ├── h2.popup-heading "Create new Project?"
 *     ├── div.clearfix.row.popup-content-part
 *     │   ├── span "Project-name:"
 *     │   ├── input.prj-name[name=name maxlength=50 pattern=…]
 *     │   ├── i.text-clear.icon-close-1
 *     │   └── span.validation-note "Minimum 3 characters"
 *     └── .popup-control-buttons (button.add.highlighted-btn[name=add_new_project autofocus]
 *         "Create", button.default-btn.not-highlighted-btn[name=cancel] "Cancel")
 *
 * Behavior (parity 83-89f):
 *  - default name `Project_{Y}{M+1}{D}_{H:M:S}` (NOT zero-padded, parity 84).
 *  - ~200ms after open: `focus()` + `select()` (parity 85); dblclick selects all.
 *  - validity = checkValidity() && length > 2 on keyup/change (dev 9e92b36370,
 *    new_project.js:74-87): fail → Create gains `.non-clickable` AND
 *    `disabled`, input gains `.invalid-field`; pass → all three reversed.
 *  - Create: re-check `< 3` → add `.invalid-field`, no submit; valid →
 *    createProject POST + write localStorage + close (parity 88).
 *  - Cancel closes (parity 89).
 *  - clear-X (`.text-clear.icon-close-1`, next DOM sibling of the input) clears
 *    the input AND revalidates — empty fails the length>2 check so Create is
 *    disabled and the input gains `.invalid-field` (per the new checkField;
 *    §10 item 13: scoped to this modal per the recommendation).
 */
interface NewProjectModalProps {
  onCreate: (name: string) => void;
  onClose: () => void;
}

/** Pattern from new_project.tmpl (letters, digits, `- + & $ ' _ : ( ) .` space). */
const NAME_PATTERN = "[A-Za-z,0-9\\-,+,&,$,',_,:,\\(,\\),., ]{3,50}";
const FOCUS_DELAY_MS = 200;

/**
 * Default name `Project_{Y}{M+1}{D}_{H:M:S}` — NOT zero-padded (new_project.js
 * `show()`), e.g. `Project_2026641_14:5:3`.
 */
function defaultProjectName(now: Date = new Date()): string {
  const y = now.getFullYear();
  const m = now.getMonth() + 1; // M+1 (getMonth is 0-based)
  const d = now.getDate();
  const h = now.getHours();
  const min = now.getMinutes();
  const s = now.getSeconds();
  return `Project_${y}${m}${d}_${h}:${min}:${s}`;
}

const NewProjectModal = ({ onCreate, onClose }: NewProjectModalProps) => {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [name, setName] = useState<string>(() => defaultProjectName());
  // `.non-clickable` + `disabled` on Create (validity fail).
  const [valid, setValid] = useState(true);
  // `.invalid-field` (red border) — toggled by checkField (dev 9e92b36370: fail
  // now ADDS it, pass removes it) and by a <3-char Create submit.
  const [invalidField, setInvalidField] = useState(false);

  // checkField: checkValidity() && length > 2 (new_project.js:76). Failure
  // disables Create (`.non-clickable` + `disabled`) AND adds `.invalid-field`;
  // success reverses all three (new_project.js:79-86).
  const checkField = useCallback((value: string) => {
    const inp = inputRef.current;
    const ok = (inp ? inp.checkValidity() : value.length <= 50) && value.length > 2;
    setValid(ok);
    setInvalidField(!ok);
  }, []);

  // Focus + select ~200ms after open (parity 85). Re-validate the default name.
  useEffect(() => {
    const t = setTimeout(() => {
      const inp = inputRef.current;
      if (inp) {
        inp.focus();
        inp.select();
        checkField(inp.value);
      }
    }, FOCUS_DELAY_MS);
    return () => clearTimeout(t);
  }, [checkField]);

  const onChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const v = e.target.value;
      setName(v);
      checkField(v);
    },
    [checkField]
  );

  // create_new_project: re-check `< 3` → add `.invalid-field`, no submit; valid
  // → dispatch create + write localStorage + close (parity 88).
  const onCreateClick = useCallback(() => {
    const value = (inputRef.current?.value ?? name).trim();
    if (value.length < 3) {
      setInvalidField(true);
      return;
    }
    try {
      // Parity: new_project.js writes localStorage 'create:new_project'.
      window.localStorage.setItem('create:new_project', value);
    } catch {
      /* localStorage may be unavailable; non-fatal */
    }
    onCreate(value);
    onClose();
  }, [name, onCreate, onClose]);

  // clear-X: empty the input + revalidate (empty ⇒ Create disabled + red
  // border via the new checkField) — §3.H / §10 item 13. Scoped to this modal
  // (the X's sibling input).
  const onClear = useCallback(() => {
    setName('');
    // Reflect the empty value to the input before reading validity.
    if (inputRef.current) inputRef.current.value = '';
    checkField('');
    inputRef.current?.focus();
  }, [checkField]);

  return (
    <div className="popup-wrapper create-popup active">
      <div className="popup-content">
        <h2 className="popup-heading">Create new Project?</h2>
        <div className="clearfix row popup-content-part">
          <span>Project-name:</span>
          <input
            ref={inputRef}
            className={`prj-name${invalidField ? ' invalid-field' : ''}`}
            type="text"
            name="name"
            maxLength={50}
            pattern={NAME_PATTERN}
            value={name}
            onChange={onChange}
            onKeyUp={(e) => checkField((e.target as HTMLInputElement).value)}
            onKeyDown={(e) => {
              // QA D4: Enter triggers Create (legacy had no Enter handling).
              // Gated on the new validity rule — the button is disabled while
              // invalid, so Enter must not create either.
              if (e.key === 'Enter') {
                e.preventDefault();
                if (valid) onCreateClick();
              }
            }}
            onDoubleClick={(e) => (e.target as HTMLInputElement).select()}
          />
          {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */}
          <i className="text-clear icon-close-1" onClick={onClear} />
          <span className="validation-note">Minimum 3 characters</span>
        </div>
        <div className="popup-control-buttons">
          <button
            className={`add highlighted-btn${valid ? '' : ' non-clickable'}`}
            name="add_new_project"
            type="button"
            autoFocus
            disabled={!valid}
            onClick={onCreateClick}
          >
            Create
          </button>
          <button
            className="default-btn not-highlighted-btn"
            name="cancel"
            type="button"
            onClick={onClose}
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};

export { defaultProjectName };
export default NewProjectModal;
